/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Text } from '@/shared/ui';
import { ImageIcon } from '@/shared/ui/radix-icons';
import { normalizeImageUrl } from '@/shared/utils/normalize-image-url';
import { StatusBadge } from './StatusBadge';
import { BusinessTypeBadge } from './BusinessTypeBadge';
import { cardPriceLabel, photoCount, specLine } from './property-card-helpers';
import { propertyAgencyCode, propertyBusinessType } from '@/shared/utils/property-display';
import { getCategoryLabel } from './PropertyFormDualPane/schema';
import type { PropertyResponseType } from '@services/housien/types';

/** Square side of the cover photo, in px. */
const PHOTO_SIZE = 84;

interface MobileCardViewProps {
  properties: PropertyResponseType[];
  onCardClick: (property: PropertyResponseType) => void;
  t: (key: string) => string;
}

/**
 * One listing as a tappable card: the photo carries the recognition, the
 * text carries the decision.
 *
 * The photo is the 160px derivative the list page already fetched
 * (`media.cover_thumbnail`), not the full upload — a screen of cards costs a
 * few KB rather than several MB, which is the difference between a list that
 * scrolls and one that stalls on a phone connection.
 */
export const MobileCardView: React.FC<MobileCardViewProps> = ({ properties, onCardClick, t }) => {
  const { t: tLocal } = useTranslation('features/properties');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {properties.map((property) => {
        const priceLabel = cardPriceLabel(property, t);
        const status = property.classification?.status;
        const businessType = propertyBusinessType(property as unknown as Record<string, unknown>);
        const specs = specLine(property, tLocal);

        return (
          <div
            key={property.id}
            data-testid="property-card"
            onClick={() => onCardClick(property)}
            style={{
              display: 'flex',
              gap: 'var(--space-3)',
              background: 'var(--surface-primary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-sm)',
              padding: 'var(--space-3)',
              cursor: 'pointer',
              transition: 'var(--transition-standard)',
              fontFamily: 'var(--font-family)',
            }}
          >
            <CoverPhoto property={property} label={tLocal('list.card.photos', { count: photoCount(property) })} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1-5)', flexWrap: 'wrap' }}>
                {/*
                  * The id reads as a chip like the two badges beside it, not
                  * as loose text: the three sit on one line, and one of them
                  * being unboxed made the row look like a rendering fault
                  * rather than a deliberate hierarchy. `tabular-nums` and not
                  * a mono face: the digits of a code somebody reads aloud
                  * still line up, without a second typeface in the row.
                  */}
                <Text
                  as="span"
                  data-testid="property-card-code"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 'var(--space-1) var(--space-2)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--surface-tertiary)',
                    color: 'var(--text-secondary)',
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: 'var(--text-meta)',
                    fontWeight: 'var(--font-semibold)' as unknown as number,
                    letterSpacing: 'var(--tracking-tight)',
                  }}
                >
                  {propertyAgencyCode(property as unknown as Record<string, unknown>) ?? property.id}
                </Text>
                <BusinessTypeBadge type={businessType} />
                <StatusBadge status={status === 'inactive' ? 'inactive' : 'active'} />
              </div>

              <Text
                as="span"
                style={{
                  fontSize: 'var(--text-body-sm)',
                  fontWeight: 'var(--font-semibold)' as unknown as number,
                  color: 'var(--text-primary)',
                  lineHeight: 'var(--leading-snug)',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {property.description?.headline || t('list.noTitle')}
              </Text>

              <Text
                as="span"
                className="truncate"
                style={{ fontSize: 'var(--text-meta)', color: 'var(--text-secondary)' }}
              >
                {specs || getCategoryLabel(property.classification?.category ?? '', t) || '—'}
              </Text>

              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-2)', marginTop: 'auto' }}>
                <Text
                  as="span"
                  style={{
                    fontSize: 'var(--text-body-sm)',
                    fontWeight: 'var(--font-bold)' as unknown as number,
                    color: 'var(--seed-base)',
                  }}
                >
                  {priceLabel}
                </Text>
                <Text
                  as="span"
                  className="truncate"
                  style={{ fontSize: 'var(--text-micro, 0.6875rem)', color: 'var(--text-tertiary)' }}
                >
                  {[property.location?.neighborhood, property.location?.city].filter(Boolean).join(', ')}
                </Text>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * The cover photo, with the photo count riding its bottom-left corner.
 *
 * A listing with no photo keeps the same 84px box rather than collapsing it:
 * the text column has to start at the same x on every card, or the list
 * develops a ragged left edge that reads as a rendering fault. The empty box
 * also says something true — this listing has no photo — which is exactly
 * what somebody scanning for incomplete listings is looking for.
 */
const CoverPhoto: React.FC<{ property: PropertyResponseType; label: string }> = ({ property, label }) => {
  const [failed, setFailed] = React.useState(false);
  const url = (property.media as { cover_thumbnail?: string } | undefined)?.cover_thumbnail;
  const count = photoCount(property);
  const showImage = Boolean(url) && !failed;

  return (
    <div
      style={{
        position: 'relative',
        width: PHOTO_SIZE,
        height: PHOTO_SIZE,
        flexShrink: 0,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-tertiary)',
        overflow: 'hidden',
      }}
    >
      {showImage ? (
        <Image
          src={normalizeImageUrl(url!)}
          alt=""
          aria-hidden="true"
          loading="lazy"
          onError={() => setFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Text
          as="span"
          aria-hidden="true"
          className="flex h-full w-full items-center justify-center"
          style={{ color: 'var(--text-tertiary)', opacity: 0.5 }}
        >
          <ImageIcon width={20} height={20} />
        </Text>
      )}

      {count > 0 && (
        <Text
          as="span"
          data-testid="property-card-photo-count"
          style={{
            position: 'absolute',
            left: 4,
            bottom: 4,
            padding: '1px 5px',
            borderRadius: 'var(--radius-sm)',
            background: 'color-mix(in srgb, var(--surface-inverse) 72%, transparent)',
            color: 'var(--text-inverse)',
            fontSize: 'var(--text-eyebrow)',
            lineHeight: 1.4,
            fontWeight: 'var(--font-semibold)' as unknown as number,
          }}
        >
          {label}
        </Text>
      )}
    </div>
  );
};
